import React from 'react';
import { useBlueprint } from '../../contexts/BlueprintContext';
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

/**
 * BlueprintIndicator - A small clickable icon that appears on UI blocks
 * When clicked, it opens the Blueprint viewer and shows content for that specific block/section
 * 
 * Usage:
 * <BlueprintIndicator 
 *   sectionId="team-contractors" 
 *   tooltip="Learn about contractor management" 
 * />
 */
const BlueprintIndicator = ({ 
  sectionId, 
  tooltip = "Click for help",
  className = "",
  size = "md" // sm, md, lg - default to md for better visibility
}) => {
  const { isOpen, openBlueprint, setActiveBlockSection } = useBlueprint();

  const sizeClasses = {
    sm: "w-3.5 h-3.5",
    md: "w-4 h-4", 
    lg: "w-5 h-5"
  };

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Set the section ID first (will be used once blueprint loads)
    if (setActiveBlockSection) {
      setActiveBlockSection(sectionId);
    }
    
    // Open blueprint if not open (this will load the blueprint)
    if (!isOpen) {
      openBlueprint();
    }
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            className={`
              inline-flex items-center justify-center
              text-[#70170F] hover:text-white
              bg-[#70170F]/20 hover:bg-[#70170F]
              border border-[#70170F]/40 hover:border-[#70170F]
              rounded-full p-1
              transition-all duration-200
              hover:scale-110
              shadow-sm hover:shadow-md
              ${className}
            `}
            data-testid={`blueprint-indicator-${sectionId}`}
            data-blueprint-section={sectionId}
            aria-label={tooltip}
          >
            <HelpCircle className={sizeClasses[size]} strokeWidth={2.5} />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-[#70170F] text-white text-xs px-2 py-1">
          <p>{tooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default BlueprintIndicator;
